<script setup>
/**
 * The sharing decision, for a note the member typed themselves.
 *
 * Notes belong to every RM member; RM AI is a Platinum capability. So the
 * sharing step cannot live only in chat — this is the same decision, reachable
 * from Favorites and from a restaurant's My Notes, with no model involved
 * (Kapil, 2026-09-21).
 *
 * What is shared is the note itself (Kapil, 2026-09-24: "it can only be a
 * private note or has to be a highlight… it can't be both"), so there is no
 * wording to edit here — the note is on screen right above, and to share
 * different words the member edits the note first.
 *
 * The audiences offered are only the ones the server will accept: sharing with
 * the restaurant needs it to be one of their Favorites. The service refuses it
 * either way — this only stops the UI offering something that would bounce.
 */
import { computed, ref } from 'vue'
import axios from '@axios'

const props = defineProps({
  entryId: { type: String, required: true },
  restaurantName: { type: String, default: '' },
  canShareWithRestaurant: { type: Boolean, default: false },

  // False when sharing a note saved earlier — "Added to your notes." is only
  // true straight after a save.
  justSaved: { type: Boolean, default: true },
})

const emit = defineEmits(['done', 'shared'])

const busy = ref(false)
const error = ref('')

const canShare = computed(() => !busy.value)

/**
 * Kapil's wording, 2026-09-22. "your note" rather than "above": the words are
 * already on screen right above, so the question names what it is asking about
 * instead of pointing at it. The parenthetical saying where a highlight lands
 * is gone — it made the sentence twice as long to read, and the confirmation
 * after they choose still tells them where it went.
 */
const question = computed(() => (props.canShareWithRestaurant && props.restaurantName
  ? `Open to sharing your note as a highlight with your Network &/or with the team at ${props.restaurantName}?`
  : 'Open to sharing your note as a highlight with your Network?'))

async function share(audiences) {
  if (!canShare.value) return
  busy.value = true
  error.value = ''
  try {
    // Each audience is its own approved share of the note itself.
    for (const audience of audiences) {
      // eslint-disable-next-line no-await-in-loop
      await axios.post(`/profile/entries/${props.entryId}/shares`, { audience })
    }
    emit('shared', { audiences })
    emit('done')
  } catch (e) {
    // The server's own sentence is the right one — it knows why it refused.
    error.value = e?.response?.data?.error || 'That could not be shared right now.'
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="share-decision">
    <p
      v-if="justSaved"
      class="share-decision__saved"
    >
      Added to your notes.
    </p>

    <p class="share-decision__question">
      {{ question }}<br>
      Or prefer to keep it private?
    </p>

    <div class="share-decision__options">
      <button
        type="button"
        class="share-decision__chip"
        :disabled="!canShare"
        @click="share(['network'])"
      >
        Network
      </button>
      <button
        v-if="canShareWithRestaurant && restaurantName"
        type="button"
        class="share-decision__chip"
        :disabled="!canShare"
        @click="share(['restaurant'])"
      >
        {{ restaurantName }}'s Team
      </button>
      <button
        v-if="canShareWithRestaurant && restaurantName"
        type="button"
        class="share-decision__chip"
        :disabled="!canShare"
        @click="share(['network', 'restaurant'])"
      >
        Both
      </button>
      <button
        type="button"
        class="share-decision__chip"
        :disabled="busy"
        @click="emit('done')"
      >
        Keep private
      </button>
    </div>

    <p
      v-if="error"
      class="share-decision__error"
    >
      {{ error }}
    </p>
  </div>
</template>

<style scoped>
.share-decision {
  padding-block: 8px;
}

.share-decision__saved {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px;
}

.share-decision__question {
  font-size: 14px;
  margin: 12px 0 8px;
}

.share-decision__options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.share-decision__chip {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.22);
  border-radius: 16px;
  font-size: 13px;
  padding: 6px 14px;
}

.share-decision__chip:disabled {
  opacity: 0.5;
}

.share-decision__error {
  color: rgb(var(--v-theme-error));
  font-size: 13px;
  margin: 8px 0 0;
}
</style>
